import {
  Outlet,
  RouterProvider,
  createRootRoute,
  createRoute,
  createRouter,
} from "@tanstack/react-router";
import { createRoot } from "react-dom/client";

import { App } from "./App";
import { ToastProvider } from "./components/Toast";
import { AuthenticatedLayout } from "./routes/__root";
import { ChangePasswordRoute } from "./routes/change-password";
import { LoginRoute } from "./routes/login";

const rootRoute = createRootRoute({
  component: () => <Outlet />,
});

// Public routes (no auth-guard layout).
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: LoginRoute,
});

// Authenticated layout — guards every nested route via /api/me.
const authedLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "_authenticated",
  component: AuthenticatedLayout,
});

const changePasswordRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: "/change-password",
  component: ChangePasswordRoute,
});

// One shared component reference (`App`) for every Nav-tab route. Same
// reference means React reconciles instead of unmounting App between routes,
// so people/projects/etc. don't refetch on every tab click. App reads the
// current pathname via useLocation to derive the active view.
const VIEW_PATHS = [
  "/",
  "/projects-view",
  "/person-view",
  "/bench",
  "/budget",
  "/reports",
  "/project-setup",
  "/people-setup",
  "/admin",
];

const viewRoutes = VIEW_PATHS.map((path) =>
  createRoute({
    getParentRoute: () => authedLayoutRoute,
    path,
    component: App,
  }),
);

const routeTree = rootRoute.addChildren([
  loginRoute,
  authedLayoutRoute.addChildren([changePasswordRoute, ...viewRoutes]),
]);

const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("#root element not found");
createRoot(rootEl).render(
  <ToastProvider>
    <RouterProvider router={router} />
  </ToastProvider>,
);
